import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  useState,
} from "react";
import {
  useGamepadPolling,
  GamepadButtonEvent,
  GamepadConnectionEvent,
} from "@onmo/gamepad-manager";
import { GamepadEmitter } from "../models/gamepadEmitter";
import { findStartElementRectById, isElementVisible } from "../models/screen";
import { PageRoutes } from "../constants/routes";
import { getIsFooterVisible } from "../utils/footer";
import { useLocation } from "react-router-dom";
import {
  getGamepadsConnected,
  getNextElement,
  IGamePad,
  onDefaultMoveAction,
  onGamepadAction,
  onMoveNextDialogElement,
} from "../models/gamepad";
import {
  animatedScrollIntoView,
  cancelScrollAnimation,
} from "../utils/smoothScroll";
import { isAndroidAppTv, isTizen, isTV } from "../utils/platform";
import { App } from "@capacitor/app";
import { EON_KEYBOARD_INPUT_ID } from "@onmo/eon-streaming";
import { useSFXSound, SFXType } from "../hooks/useSFXSound";
import { useScrollRestore } from "../hooks/useScrollRestore";
import { useLatestRef } from "../hooks/useLatestRef";
import {
  isMediaPreviewVideoPlaying,
  requestMediaPreviewRevert,
} from "../utils/media-preview-revert";
import {
  isGamepadMode,
  setCrossMode,
  useCrossInputListener,
  onCrossModeChange,
} from "../hooks/useCrossInput";
import {
  selectIsDesktopView,
  selectIsMobile,
  setInputMode,
} from "../store/slices/appState";
import { useAppDispatch, useAppSelector } from "../components/hooks";
import { SettingsView } from "../constants/settings";
import { setMenuOpen } from "../store/slices/gamePlay";
import { useBackNavigation } from "../hooks/useBackNavigation";

export const keyToActionMap: Record<string, GamepadAction> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  Enter: "submit",
  // back
  Escape: "back",
  XF86Back: "back", // tizen
  GoBack: "back", // webos
} as const;

export const keyMoveToActionMap: Record<string, GamepadAction> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
} as const;

export type ICallbackGamepad = {
  onMoveAction?: (
    activeElement?: HTMLElement,
    action?: "left" | "right" | "up" | "down",
  ) => HTMLElement | undefined;
  onBackAction?: (activeElement?: HTMLElement) => void;
  onSubmitAction?: (activeElement?: HTMLElement) => void;
};

interface IGamepadContext {
  gamepadEmitter: GamepadEmitter;
  setActiveElement: (element?: HTMLElement) => void;
  setActiveDefaultElement: () => void;
  activeElement: HTMLElement | undefined;
  setCallback: (callback?: ICallbackGamepad) => ICallbackGamepad | undefined;
  cleanup: () => void;
  gamepads?: IGamePad[];
  playSFX: (type: SFXType) => void;
  onToggleMenuGameplay: () => void;
  isOpenKeyboard?: boolean;
}

// --- Active element external store (for useSyncExternalStore) ---
type ActiveElementListener = () => void;
let activeElementStore: HTMLElement | undefined;
const activeElementListeners = new Set<ActiveElementListener>();

function getActiveElementSnapshot(): HTMLElement | undefined {
  return activeElementStore;
}

function subscribeActiveElement(listener: ActiveElementListener): () => void {
  activeElementListeners.add(listener);
  return () => activeElementListeners.delete(listener);
}

// Tracks whether a content element (vs menu item) is focused.
// Computed inside setActiveElementStore, shares the same subscription.
let contentActiveStore = false;

function getContentActiveSnapshot(): boolean {
  return contentActiveStore;
}

export function useContentActive(): boolean {
  return useSyncExternalStore(subscribeActiveElement, getContentActiveSnapshot);
}

function setActiveElementStore(el?: HTMLElement) {
  if (activeElementStore === el) return;
  activeElementStore = el;
  if (el) {
    const dataGamepad = el.getAttribute("data-gamepad");
    contentActiveStore = !dataGamepad?.startsWith("menu-");
  } else {
    // Element cleared (mouse mode) — content is considered active
    contentActiveStore = true;
  }
  activeElementListeners.forEach((l) => l());
}

export const activeElementsByPage = new Map<string, string>();

const REMEMBER_ACTIVE_ELEMENT_PATHS: string[] = [
  PageRoutes.Home,
  PageRoutes.EssentialPass,
  PageRoutes.InstallGame,
  PageRoutes.Settings,
  PageRoutes.Search,
  PageRoutes.LegalInformation,
  PageRoutes.OpenSource,
];

export interface IHeaderRef {
  open: () => void;
  onPopupClickSubmit: () => void;
  onPopupClickCancel: () => void;
}

type GamepadAction =
  | "up"
  | "down"
  | "left"
  | "right"
  | "submit"
  | "back"
  | "x-square"
  | "y-triangle"
  | "lb-l1"
  | "rb-r1"
  | "rt-r2"
  | "lt-l2"
  | "menu"
  | "touchpad"
  | "menu-hold"
  | "home"
  | "snackbar-video"
  | "other";

const holdDelay = 1500;
const minDelay = 50;
const repeatGamepadDelay = 150;

function getExtraOffsetBottom(item: HTMLElement, isDesktop: boolean): number {
  const override = Number(item.getAttribute("data-scroll-offset-bottom"));
  if (Number.isFinite(override) && override > 0) return override;
  return isDesktop ? 42 : 106;
}

function getScrollInline(
  item: HTMLElement,
  dataGamepad: string | null,
): ScrollLogicalPosition {
  const override = item.getAttribute(
    "data-scroll-inline",
  ) as ScrollLogicalPosition | null;
  if (override) return override;
  return dataGamepad === "menu-/" ? "center" : "start";
}

function getDialogDefaultElement(dialog: Element) {
  return (
    dialog.querySelector<HTMLElement>(`[data-gamepad="default-selected"]`) ||
    dialog.querySelector<HTMLElement>("[data-gamepad]")
  );
}

export const GamepadContext = createContext<IGamepadContext | null>(null);

interface GamepadProviderProps {
  children: React.ReactNode;
}

export const GamepadProvider: React.FC<GamepadProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const gamepadEmitter = useRef<GamepadEmitter>(new GamepadEmitter());
  const dialogRef = useRef<HTMLDialogElement>();
  const keyboardRef = useRef<HTMLElement>();
  const lastElementRef = useRef<HTMLElement>();
  const activeElementRef = useRef<HTMLElement>();
  const callbackRef = useRef<ICallbackGamepad>();
  const lastMoveTimeRef = useRef<number>(0);
  const lastActionRef = useRef<GamepadAction>();
  const isInputRepeatRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  const keyboardJustClosedRef = useRef<boolean>(false);
  const lastGamepadActionRef = useRef<{
    action: GamepadAction;
    startTime: number;
  }>();
  const lastMoveRepeatTimeRef = useRef<number>(Infinity);
  const keyboardEscTimerRef = useRef(0);
  const keyboardJustClosedTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [gamepads, setGamepads] = useState<IGamePad[]>(getGamepadsConnected());

  const justPressedButtonsRef = useRef<Set<number>>(new Set());
  const setActiveElementRef = useRef<(element?: HTMLElement) => void>(() => {});

  const [isOpenKeyboard, setIsOpenKeyboard] = useState(false);
  const { isMenuOpen, loadingStatus } = useAppSelector(
    (state) => state.gamePlay,
  );
  const { currentSnackbarMessage, isSnackbarVisible } = useAppSelector(
    (state) => state.gamePlay,
  );
  const goBack = useBackNavigation();

  const isMenuOpenRef = useRef(false);
  const loadingStatusRef = useRef(loadingStatus);

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  useEffect(() => {
    loadingStatusRef.current = loadingStatus;
  }, [loadingStatus]);

  const shouldPollGamepad = useCallback((event?: GamepadButtonEvent) => {
    const isInGameplay = window.location.pathname.startsWith(
      PageRoutes.GamePlay,
    );

    // Check if Menu button is pressed during gameplay to open side menu
    if (
      isInGameplay &&
      !isMenuOpenRef.current &&
      !keyboardRef.current &&
      event &&
      (event.buttonIndex === 9 || event.buttonIndex === 5)
    ) {
      const gamepad = event.enhanced.gamepad;

      const isStandardMapping = gamepad.mapping === "standard";
      const isTizenXbox = isTizen && event.enhanced.deviceType === "xbox";

      const isMenuPressed = isStandardMapping
        ? gamepad.buttons?.[9]?.pressed
        : isTizenXbox
        ? gamepad.buttons?.[5]?.pressed
        : gamepad.buttons?.[9]?.pressed;

      if (isMenuPressed) return true;
    }

    const isActiveInputGamePlay =
      document.activeElement?.tagName === "INPUT" && isInGameplay;
    const isDialogOpen = !!dialogRef.current;
    const isTVKeyboardOpen = !!keyboardRef.current;
    const isLoading = loadingStatusRef.current !== "Done";
    const shouldPoll =
      !isInGameplay ||
      isMenuOpenRef.current ||
      isActiveInputGamePlay ||
      isDialogOpen ||
      isTVKeyboardOpen ||
      isLoading ||
      isSnackbarVisibleRef.current;

    return shouldPoll;
  }, []);

  const onGamepadButton = useCallback(
    (event: GamepadButtonEvent) => {
      if (!shouldPollGamepad(event)) return;

      if (event.pressed) {
        justPressedButtonsRef.current.add(event.buttonIndex);
        if (!animationFrameRef.current) {
          animationFrameRef.current =
            requestAnimationFrame(updateGamepadStatus);
        }
      }
    },
    [shouldPollGamepad],
  );

  const onGamepadAxis = useCallback(() => {
    if (!shouldPollGamepad()) return;

    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(updateGamepadStatus);
    }
  }, [shouldPollGamepad]);

  const onGamepadConnect = useCallback((_event: GamepadConnectionEvent) => {
    const currentActiveMenuElement = document?.querySelector(
      `[data-gamepad="menu-${window.location.pathname}"]`,
    );
    const firstElement = document?.querySelector(`[data-gamepad]`);
    if (currentActiveMenuElement) {
      setActiveElementRef.current(currentActiveMenuElement as HTMLElement);
    } else {
      setActiveElementRef.current(firstElement as HTMLElement);
    }
    setGamepads(getGamepadsConnected());
  }, []);

  const onGamepadDisconnect = useCallback((_event: GamepadConnectionEvent) => {
    const currentGamepad = getGamepadsConnected();
    setGamepads(currentGamepad);
    if (!currentGamepad.length && animationFrameRef.current !== null) {
      setActiveElementRef.current(undefined);
      dispatch(setInputMode(isTV ? "remote" : "mouse"));
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const { getGamepads } = useGamepadPolling({
    pollRate: 60,
    debug: false,
    onButton: onGamepadButton,
    onAxis: onGamepadAxis,
    onConnect: onGamepadConnect,
    onDisconnect: onGamepadDisconnect,
  });

  // Cache for DOM elements to reduce queries during rapid horizontal movement
  const gamepadElementsCacheRef = useRef<{
    elements: HTMLElement[];
    lastUpdate: number;
  }>({ elements: [], lastUpdate: 0 });

  // use latest value ref (playSFX)
  const playSFX = useSFXSound();
  const playSFXRef = useLatestRef(playSFX);

  // Viewport class - mobile/tablet hide the footer, desktop shows it.
  const isDesktop = useAppSelector(selectIsDesktopView);
  const isDesktopRef = useLatestRef(isDesktop);
  const isMobile = useAppSelector(selectIsMobile);
  const isMobileRef = useLatestRef(isMobile);

  const currentSnackbarMessageRef = useLatestRef(currentSnackbarMessage);
  const isSnackbarVisibleRef = useLatestRef(isSnackbarVisible);

  const crossModeRef = useRef<string>(
    document.body.getAttribute("data-input-mode") ?? "",
  );

  useEffect(() => {
    return onCrossModeChange((mode) => {
      crossModeRef.current = mode;
    });
  }, []);

  const navHeightRef = useRef<number>(0);

  useEffect(() => {
    const updateNavHeight = () => {
      const navBar = document.getElementById("navigation-bar");
      navHeightRef.current = navBar?.offsetHeight || 0;
    };
    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  const setCallback = useCallback(
    (callback?: ICallbackGamepad): ICallbackGamepad | undefined => {
      const prev = callbackRef.current;
      callbackRef.current = callback;
      return prev;
    },
    [],
  );

  const cleanup = useCallback(() => {
    callbackRef.current = undefined;
  }, []);

  const deactivateElement = (item?: HTMLElement) => {
    if (!item) return;
    const activeClass = item.getAttribute("data-selected-class");
    activeClass && item.classList.remove(activeClass);
    // Exit focus input field if available
    const inputField = item.querySelector("input");
    if (inputField instanceof HTMLElement) {
      inputField?.blur();
    } else if (item.tagName === "INPUT") {
      item?.blur();
    }
  };

  const setActiveElement = useCallback((element?: HTMLElement) => {
    const activateElement = (item?: HTMLElement) => {
      if (!item) return;

      // Pre-calculate safe scroll offsets
      const dataGamepad = item.getAttribute("data-gamepad");
      const activeClass = item.getAttribute("data-selected-class");
      activeClass && item.classList.add(activeClass);
      setActiveElementStore(item);

      // Skip scroll if element opts out
      if (item.getAttribute("data-gamepad-no-auto-scroll") === "true") return;

      const scrollInline = getScrollInline(item, dataGamepad);

      // True when current action is from auto-repeat (held button on remote).
      // Smooth scroll uses slower duration when held (smoother continuous nav)
      // and faster duration on single press (snappier, less perceived jitter).
      const isRepeating = isInputRepeatRef.current;

      if (dataGamepad?.startsWith("menu-")) {
        if (lastActionRef.current !== "up") return;
        // Scroll the nav bar horizontally so the menu item is visible
        animatedScrollIntoView(item, {
          axis: "x",
          block: "nearest",
          inline: scrollInline,
          isRepeating,
        });
        animatedScrollIntoView(document.body, {
          axis: "y",
          block: "start",
          isRepeating,
        });
      } else {
        const isHorizontalScroll = ["left", "right"].includes(
          lastActionRef.current!,
        );

        if (isHorizontalScroll) {
          animatedScrollIntoView(item, {
            axis: "x",
            block: "nearest",
            inline: scrollInline,
            isRepeating,
          });
        } else {
          const hasFooterShow = getIsFooterVisible();

          // on mobile/tablet we don't show the footer, so footerHeight = 0
          const footerHeight = isDesktopRef.current ? 41 : 0;
          const extraOffsetBottom = getExtraOffsetBottom(
            item,
            isDesktopRef.current,
          );
          const offsetBottom =
            extraOffsetBottom + (hasFooterShow ? footerHeight : 0);

          animatedScrollIntoView(item, {
            axis: "both",
            block: "end",
            inline: scrollInline,
            offsetBottom,
            isRepeating,
          });
        }
      }
    };

    deactivateElement(activeElementRef.current);
    if (element) {
      activateElement(element);
    } else {
      setActiveElementStore(undefined);
    }
    activeElementRef.current = element;
  }, []);
  setActiveElementRef.current = setActiveElement;

  const setActiveDefaultElement = useCallback(
    (preLocation?: string) => {
      if (preLocation?.startsWith(PageRoutes.GamePlay) || !isGamepadMode())
        return;

      const dialog = dialogRef.current;
      if (dialog) {
        const dialogElement = getDialogDefaultElement(dialog);
        if (dialogElement) {
          setActiveElement(dialogElement);
          return;
        }
      }

      const location = window.location.pathname;
      const currentActiveMenuElement = document?.querySelector(
        `[data-gamepad="menu-${location}"]`,
      );
      if (currentActiveMenuElement) {
        setActiveElement(currentActiveMenuElement as HTMLElement);
      } else {
        setActiveElement(
          document.querySelector("[data-gamepad]") as HTMLElement,
        );
      }
    },
    [setActiveElement],
  );

  // Helper to get cached gamepad elements (reduces DOM queries during rapid movement)
  const getCachedGamepadElements = useCallback(() => {
    const now = Date.now();
    const cache = gamepadElementsCacheRef.current;

    // Cache for 500ms on TV to aggressively reduce DOM queries during movement
    const cacheTime = isTV ? 500 : 100;
    if (cache.elements.length > 0 && now - cache.lastUpdate < cacheTime) {
      return cache.elements;
    }

    const elements = Array.from(
      document?.querySelectorAll("[data-gamepad]") || [],
    ) as HTMLElement[];
    gamepadElementsCacheRef.current = { elements, lastUpdate: now };
    return elements;
  }, []);

  const onToggleMenuGameplay = () => {
    if (window.location.pathname.startsWith(PageRoutes.GamePlay)) {
      const menuGamePlayElement = document.querySelector(
        "[data-gamepad-menu]",
      ) as HTMLElement;
      if (menuGamePlayElement) {
        menuGamePlayElement.click();
      }
    }
  };

  const handleGamepadEvent = useCallback((event: string) => {
    const savedGamepad = activeElementsByPage.get(window.location.pathname);

    const activeElement = activeElementRef.current;
    const listElement = getCachedGamepadElements();
    const activeDataGamepad = activeElement?.getAttribute("data-gamepad");
    const firstElement = listElement.find(
      (item) => !item.getAttribute("data-gamepad")?.startsWith("menu-"),
    );

    const currentActiveMenuElement = listElement.find(
      (item) =>
        item.getAttribute("data-gamepad") ===
        `menu-${window.location.pathname}`,
    );
    const isActiveMenu = activeDataGamepad?.startsWith("menu-");

    const onMoveAction = (
      action: "left" | "right" | "up" | "down",
      activeElement?: HTMLElement,
    ) => {
      // If on navbar and press down, restore last content element.
      if (
        isActiveMenu &&
        savedGamepad &&
        window.location.pathname !== PageRoutes.Settings
      ) {
        if (action === "down") {
          const target = document.querySelector<HTMLElement>(
            `[data-gamepad-id="${savedGamepad}"]`,
          );
          playSFXRef.current("movement");
          if (target) setActiveElement(target);
          return;
        }
      }

      if (!activeElement) {
        // fix flickering issue, Queue a RAF fallback in case useCrossInputListener is not triggered
        requestAnimationFrame(() => {
          if (!activeElementRef.current) {
            setActiveDefaultElement();
          }
        });
        return;
      }

      if (!isElementVisible(activeElement)) {
        setActiveDefaultElement();
        activeElement = activeElementRef.current;
        return;
      }

      let nextEl: HTMLElement | undefined;
      if (dialogRef.current) {
        // If there is a dialog open
        const dialogElements =
          dialogRef.current?.querySelectorAll("[data-gamepad]");
        const listDialogElement = Array.from(
          dialogElements || [],
        ) as HTMLElement[];
        nextEl = onMoveNextDialogElement({
          activeElement,
          action,
          listElement: listDialogElement,
        });
      } else if (keyboardRef.current) {
        const keyboardElements =
          keyboardRef.current?.querySelectorAll("[data-gamepad]");
        const listKeyboardElement = Array.from(
          keyboardElements || [],
        ) as HTMLElement[];
        nextEl = getNextElement(listKeyboardElement, activeElement, action);
      } else if (callbackRef.current?.onMoveAction) {
        nextEl = callbackRef.current?.onMoveAction?.(activeElement, action);
      } else {
        nextEl = onDefaultMoveAction(activeElement, action);
      }
      if (!nextEl) return;

      const commit = () => {
        playSFXRef.current("movement", !!dialogRef.current);
        setActiveElement(nextEl);
      };

      if (isMediaPreviewVideoPlaying()) {
        // Wait 1 frame for iframe unmount before scrolling to avoid jank.
        requestMediaPreviewRevert();
        requestAnimationFrame(commit);
        return;
      }
      commit();
    };

    const onMoveMenu = (action: "left" | "right") => {
      if (keyboardRef.current) return;
      const listMenuElement = listElement.filter((item) =>
        item.getAttribute("data-gamepad")?.startsWith("menu-"),
      );
      if (!listMenuElement.length || !currentActiveMenuElement) return;
      const nextEl = getNextElement(
        listMenuElement,
        currentActiveMenuElement,
        action,
      );
      if (!nextEl) return;
      lastActionRef.current = action;
      playSFXRef.current("movement");
      setActiveElement(nextEl);
      if (nextEl.getAttribute("data-gamepad") !== "menu-logout") {
        nextEl.click();
      }
    };

    const onCloseDialog = (isBack?: boolean) => {
      const closeBtn = document.querySelector(
        "[id=close-dialog]",
      ) as HTMLButtonElement;

      /**
       * if dialog dont have closeBtn (id="close-dialog")
       * But dialog is still have onClose event --> dispath
       */
      if (!closeBtn) {
        dialogRef.current!.dispatchEvent(new Event("cancel"));
        playSFXRef.current("back");
        return;
      }
      isBack && closeBtn?.click();
      dialogRef.current = undefined;
      if (isElementVisible(lastElementRef.current)) {
        setActiveElement(lastElementRef.current);
      } else {
        // Last element not found in DOM
        setActiveDefaultElement();
      }
    };

    // Save active element for scroll restore
    const setActiveElementsByPage = () => {
      if (!REMEMBER_ACTIVE_ELEMENT_PATHS.includes(window.location.pathname))
        return;

      const dataGamepadId = activeElement?.getAttribute("data-gamepad-id");
      if (dataGamepadId) {
        activeElementsByPage.set(window.location.pathname, dataGamepadId);
      }
    };
    const removeActiveElementsByPage = () => {
      if (isActiveMenu) return;
      activeElementsByPage.delete(window.location.pathname);
    };

    const onClickElement = () => {
      setActiveElementsByPage();
      lastActionRef.current = undefined;
      const activeDataGamepad = activeElement?.getAttribute("data-gamepad");

      if (isActiveMenu) playSFXRef.current("selection");
      if (activeDataGamepad?.startsWith("menu-/game/")) {
        // Launch the game if the active element is a game
        const playButton = document.getElementById("button-play");
        if (playButton) playButton.click();
      } else if (isActiveMenu && activeDataGamepad !== `menu-logout`) {
        let nextActive: HTMLElement | undefined;
        if (savedGamepad) {
          const target = document.querySelector<HTMLElement>(
            `[data-gamepad-id="${savedGamepad}"]`,
          );
          if (target) {
            setActiveElement(target || firstElement);
            nextActive = target;
          }
        } else if (
          firstElement?.getAttribute("data-gamepad")?.startsWith("slide-")
        ) {
          const slideElement = findStartElementRectById("slide-", listElement);
          setActiveElement(slideElement);
        } else {
          setActiveElement(firstElement);
          nextActive = firstElement;
        }
        const innerInput = nextActive?.querySelector<HTMLInputElement>(
          "input:not([disabled])",
        );
        if (innerInput) {
          innerInput.focus();
          innerInput.click();
        }
      } else if (callbackRef.current?.onSubmitAction) {
        callbackRef.current?.onSubmitAction(activeElement);
        if (activeElement?.id === "close-dialog") {
          onCloseDialog();
        }
      } else {
        activeElement?.click();
        if (activeElement?.id === "close-dialog") {
          onCloseDialog();
        }
      }
    };

    const onOpenKeyboardGamePlay = () => {
      const menuKeyboardButton = document.getElementById("keyboard-button");
      if (menuKeyboardButton) {
        menuKeyboardButton.click();
      }
    };

    const onOpenSnackbarVideo = () => {
      if (
        !window.location.pathname.startsWith(PageRoutes.GamePlay) ||
        !currentSnackbarMessageRef.current?.videoURL ||
        !isSnackbarVisibleRef.current
      ) {
        return;
      }

      const videoButton = document.getElementById("snackbar-video-button");
      if (videoButton) {
        videoButton.click();
      }
    };

    const onClickBack = (activeElement?: HTMLElement) => {
      lastActionRef.current = undefined;
      const quitButton = document.getElementById("footer-quit-button");
      const isActiveConsoleMenu = activeDataGamepad === "menu-/";
      const inGamePlay = window.location.pathname.startsWith(
        PageRoutes.GamePlay,
      );

      if (
        !dialogRef.current &&
        !isActiveConsoleMenu &&
        (quitButton || !inGamePlay)
      ) {
        playSFXRef.current("back");
      }

      if (dialogRef.current) {
        if (isMediaPreviewVideoPlaying()) {
          requestMediaPreviewRevert();
          return;
        }
        onCloseDialog(true);
      } else if (keyboardRef.current) {
        const backButton = keyboardRef.current.querySelector(
          `[data-gamepad="tvkb-hide"]`,
        ) as HTMLElement;
        backButton?.click();
      } else if (callbackRef.current?.onBackAction) {
        callbackRef.current?.onBackAction(activeElement);
      } else if (inGamePlay) {
        if (keyboardJustClosedRef.current) {
          // Back fired multiple times for one press — keyboard was just closed, skip quit
          return;
        }

        if (isMenuOpenRef.current) {
          // When menu is open in gameplay, close the menu
          if (isMobileRef.current) {
            const gamePlayMenuPopup = document.getElementById(
              "gameplay-menu-popup",
            );

            if (gamePlayMenuPopup) {
              playSFXRef.current("back");
              dispatch(setMenuOpen(false));
            }

            return;
          }

          const menuToggleButton = document.querySelector(
            '[data-gamepad-menu="menu-control"]',
          ) as HTMLElement;

          if (menuToggleButton) {
            menuToggleButton.click();
          }

          return;
        } else if (quitButton) {
          quitButton?.click();
        }
      } else if (isActiveMenu) {
        if (window.location.pathname !== PageRoutes.Home) {
          deactivateElement(activeElement);
          const consoleMenuElement = document.querySelector(
            '[data-gamepad="menu-/"]',
          ) as HTMLElement;
          setActiveElement(consoleMenuElement);
          consoleMenuElement.click();
        }
      } else if (currentActiveMenuElement) {
        setActiveElementsByPage();
        setActiveElement(currentActiveMenuElement);
      } else {
        goBack();
      }
    };

    // If TV keyboard is open, blur it when pressing buttons that dismiss it.
    // Direction buttons navigate within the keyboard so they must not blur.
    // Only check on the GamePlay route to avoid unnecessary DOM lookups elsewhere.
    const isDismissEvent =
      event === "back" || event === "menu-hold" || event === "home";

    if (
      isDismissEvent &&
      window.location.pathname.startsWith(PageRoutes.GamePlay)
    ) {
      const keyboardInput = document.getElementById(
        EON_KEYBOARD_INPUT_ID,
      ) as HTMLInputElement | null;
      if (keyboardInput && document.activeElement === keyboardInput) {
        keyboardInput.blur();
        if (event === "back") {
          keyboardJustClosedRef.current = true;
          return;
        }
      }
    }

    switch (event) {
      case "right":
      case "left":
      case "up":
      case "down":
        lastActionRef.current = event;
        removeActiveElementsByPage();
        return onMoveAction(event, activeElement);
      case "lb-l1":
        return onMoveMenu("left");
      case "rb-r1":
        return onMoveMenu("right");
      case "submit":
        return onClickElement();
      case "back":
        return onClickBack(activeElement);
      case "menu-hold":
      case "home":
        return onToggleMenuGameplay();
      case "x-square":
        return onOpenKeyboardGamePlay();
      case "y-triangle":
      case "snackbar-video":
        return onOpenSnackbarVideo();
      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Listen for changes dialog to the document
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const dialog =
              node.tagName === "DIALOG" ? node : node.querySelector("dialog");
            if (dialog instanceof HTMLDialogElement) {
              dialogRef.current = undefined;
            } else if (node.id === "keyboard-container") {
              setIsOpenKeyboard(false);
              setActiveElement(lastElementRef.current);
              keyboardRef.current = undefined;
            }
          }
        });
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const dialog =
              node.tagName === "DIALOG" ? node : node.querySelector("dialog");
            if (dialog instanceof HTMLDialogElement) {
              dialogRef.current = dialog;
              if (isElementVisible(activeElementRef.current)) {
                lastElementRef.current = activeElementRef.current;
              }
              const defaultElement = getDialogDefaultElement(dialog);
              defaultElement?.scrollIntoView();
              if (isGamepadMode())
                setActiveElement(defaultElement ?? undefined);
            } else if (node.id === "keyboard-container") {
              setIsOpenKeyboard(true);
              lastElementRef.current = activeElementRef.current;
              keyboardRef.current = node;
              const firstElement = node.querySelector(
                "[data-gamepad]",
              ) as HTMLElement;
              setActiveElement(firstElement);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel any in-progress scroll animation when route changes
  useEffect(() => {
    cancelScrollAnimation();
    // Clear element cache on route change since gamepad elements change
    gamepadElementsCacheRef.current = { elements: [], lastUpdate: 0 };
  }, [location.pathname]);

  useEffect(() => {
    const emitter = gamepadEmitter.current;
    emitter.on("action", handleGamepadEvent);
    // Clean up when component unmounts
    return () => {
      emitter.off("action", handleGamepadEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateGamepadStatus = useCallback(() => {
    const _gpList = getGamepads();

    const gp = _gpList[0] || _gpList[1] || _gpList[2] || _gpList[3];

    if (gp) {
      const currentTime = Date.now();
      const { action, gamepadType } = onGamepadAction(
        _gpList,
        justPressedButtonsRef.current,
      );
      justPressedButtonsRef.current.clear();

      if (!action && lastGamepadActionRef.current) {
        if (
          lastGamepadActionRef.current.action === "menu" &&
          lastGamepadActionRef.current.startTime !== Infinity
        ) {
          gamepadEmitter.current.emit("action", "menu-hold-cancel");
        } else if (lastGamepadActionRef.current.action === "submit") {
          gamepadEmitter.current.emit(
            "action",
            lastGamepadActionRef.current.action,
          );
        } else if (
          currentTime - lastGamepadActionRef.current.startTime > minDelay &&
          currentTime - lastMoveTimeRef.current > minDelay
        ) {
          lastMoveRepeatTimeRef.current = Infinity;
          lastMoveTimeRef.current = currentTime;
          gamepadEmitter.current.emit(
            "action",
            lastGamepadActionRef.current.action,
          );
        }
      } else if (lastGamepadActionRef.current?.action === "menu") {
        if (currentTime - lastGamepadActionRef.current.startTime > holdDelay) {
          lastGamepadActionRef.current = {
            action: "menu",
            startTime: Infinity, // Prevent further holds until button is released
          };
          gamepadEmitter.current.emit("action", "menu-hold");
        }
      } else if (action && lastGamepadActionRef.current) {
        // Only repeat the last gamepad action if it was pressed for at least 300ms
        if (currentTime - lastGamepadActionRef.current.startTime > 300) {
          lastMoveRepeatTimeRef.current = currentTime;
          lastMoveTimeRef.current = currentTime;
          lastGamepadActionRef.current = { action, startTime: currentTime };
          gamepadEmitter.current.emit(
            "action",
            lastGamepadActionRef.current.action,
          );
          // Only the last gamepad action every 120ms
        } else if (
          currentTime - lastMoveRepeatTimeRef.current >
          repeatGamepadDelay
        ) {
          lastMoveRepeatTimeRef.current = currentTime;
          gamepadEmitter.current.emit(
            "action",
            lastGamepadActionRef.current.action,
          );
        }
      }

      if (action) {
        const inputMode =
          (!gamepadType || gamepadType === "generic") && isTV
            ? "remote"
            : "gamepad";
        dispatch(setInputMode(inputMode));
        if (crossModeRef.current !== "gamepad") {
          crossModeRef.current = "gamepad";
          setCrossMode("gamepad");
        }
      } else {
        isInputRepeatRef.current = false;
        if (keyboardJustClosedRef.current) {
          if (!keyboardJustClosedTimerRef.current) {
            keyboardJustClosedTimerRef.current = setTimeout(() => {
              keyboardJustClosedRef.current = false;
              keyboardJustClosedTimerRef.current = null;
            }, 300);
          }
        }
      }

      if (action) {
        if (!lastGamepadActionRef.current) {
          lastGamepadActionRef.current = { action, startTime: currentTime };
          lastMoveTimeRef.current = currentTime;
          if (action === "menu") {
            gamepadEmitter.current.emit("action", "menu-hold-start");
          }
        }
      } else {
        lastGamepadActionRef.current = undefined;
      }
    }

    // Continue only while there is pending timing state; onButton/onAxis restarts on next input
    if (lastGamepadActionRef.current) {
      animationFrameRef.current = requestAnimationFrame(updateGamepadStatus);
    } else {
      animationFrameRef.current = null;
    }
  }, [getGamepads, dispatch, isTV]);

  useEffect(() => {
    let lastTimer = Date.now();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "F12" && e.shiftKey) {
        e.preventDefault();
        if (!e.repeat) {
          gamepadEmitter.current.emit("action", "menu-hold");
        }
        return;
      }

      if (Object.keys(keyToActionMap).includes(e.key)) {
        e.preventDefault();
        if (e.key === "Escape") {
          keyboardEscTimerRef.current = Date.now();
          return;
        }
        // Back button TV
        if (e.key === "XF86Back" || e.key === "GoBack") onToggleMenuGameplay();

        if (!lastTimer || Date.now() - lastTimer > repeatGamepadDelay) {
          isInputRepeatRef.current = e.repeat;
          const action = keyToActionMap[e.key];
          if (action) gamepadEmitter.current.emit("action", action);
          lastTimer = Date.now();
        }
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      if (Object.keys(keyToActionMap).includes(e.key)) {
        lastTimer = 0;
      }
      if (e.key === "F1" && e.shiftKey) {
        if (
          window.location.pathname.startsWith(PageRoutes.GamePlay) &&
          currentSnackbarMessageRef.current?.videoURL &&
          isSnackbarVisibleRef.current
        ) {
          gamepadEmitter.current.emit("action", "snackbar-video");
        }
        return;
      }

      if (e.key === "Escape" && keyboardEscTimerRef.current) {
        const holdDuration = Date.now() - keyboardEscTimerRef.current;
        keyboardEscTimerRef.current = 0;

        // ESC pressed while in fullscreen could trigger the "back" action (which would open the quit popup).
        if (
          holdDuration < 200 ||
          window.location.pathname.startsWith(PageRoutes.GamePlay)
        ) {
          gamepadEmitter.current.emit("action", "back");
        }
      }

      if (e.key === "Escape" || e.key === "XF86Back" || e.key === "GoBack") {
        if (
          keyboardJustClosedRef.current &&
          !keyboardJustClosedTimerRef.current
        ) {
          keyboardJustClosedTimerRef.current = setTimeout(() => {
            keyboardJustClosedRef.current = false;
            keyboardJustClosedTimerRef.current = null;
          }, 300);
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    const state = location.state as { settingsView?: SettingsView } | null;
    if (state?.settingsView && location.pathname === PageRoutes.Settings) {
      return;
    }
    setActiveDefaultElement(location.pathname);
  }, [location.pathname, setActiveDefaultElement]);

  useScrollRestore({ setActiveElement });
  useCrossInputListener({
    setActiveElement,
    removeSavedElement: () =>
      activeElementsByPage.delete(window.location.pathname),
  });

  // Stable wrapper that always reads the latest ref value
  const playSFXStable = useCallback((...args: Parameters<typeof playSFX>) => {
    playSFXRef.current(...args);
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith(PageRoutes.Game)) return;
    requestAnimationFrame(() => {
      if (
        !activeElementRef.current ||
        !isElementVisible(activeElementRef.current)
      ) {
        setActiveDefaultElement();
      }
    });
  }, [location.pathname, setActiveDefaultElement]);

  useEffect(() => {
    if (!isAndroidAppTv) return;
    App.addListener("backButton", () => {
      console.log("GamepadProvider: Android back button pressed");
      gamepadEmitter.current.emit("action", "back");
      onToggleMenuGameplay();
    });
  }, []);

  const contextValue = {
    gamepadEmitter: gamepadEmitter.current,
    setActiveElement,
    activeElement: activeElementRef.current,
    setCallback,
    cleanup,
    gamepads,
    playSFX: playSFXStable,
    setActiveDefaultElement,
    onToggleMenuGameplay,
    isOpenKeyboard,
  };
  return (
    <GamepadContext.Provider value={contextValue}>
      {children}
    </GamepadContext.Provider>
  );
};

export const useGamepadContext = () => {
  const context = useContext(GamepadContext);
  if (context === null) {
    throw new Error("useGamepadContext must be used within a GamepadProvider");
  }
  return context;
};

export const useActiveElement = (): HTMLElement | undefined => {
  return useSyncExternalStore(subscribeActiveElement, getActiveElementSnapshot);
};

export { subscribeActiveElement, getActiveElementSnapshot };
